const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const snapToGrid = (value, gridSize = 8) => {
  const unit = Math.max(1, toNumber(gridSize, 8));
  return Math.round(toNumber(value, 0) / unit) * unit;
};

export const snapRect = (rect, gridSize = 8) => ({
  ...rect,
  x: snapToGrid(rect.x, gridSize),
  y: snapToGrid(rect.y, gridSize),
  width: Math.max(24, snapToGrid(rect.width, gridSize)),
  height: Math.max(18, snapToGrid(rect.height, gridSize)),
});

export const clampRect = (rect, canvas) => {
  const canvasWidth = Math.max(24, toNumber(canvas?.width, 0));
  const canvasHeight = Math.max(18, toNumber(canvas?.height, 0));
  const width = Math.max(24, Math.min(canvasWidth, toNumber(rect.width, 24)));
  const height = Math.max(18, Math.min(canvasHeight, toNumber(rect.height, 18)));
  const maxX = Math.max(0, canvasWidth - width);
  const maxY = Math.max(0, canvasHeight - height);
  const x = Math.max(0, Math.min(maxX, toNumber(rect.x, 0)));
  const y = Math.max(0, Math.min(maxY, toNumber(rect.y, 0)));
  return { ...rect, x, y, width, height };
};

export const alignBlockLeft = (block) => ({ ...block, x: 0 });
export const alignBlockCenterX = (block, canvas) => ({
  ...block,
  x: Math.max(0, (toNumber(canvas?.width, 0) - toNumber(block?.width, 0)) / 2),
});
export const alignBlockRight = (block, canvas) => ({
  ...block,
  x: Math.max(0, toNumber(canvas?.width, 0) - toNumber(block?.width, 0)),
});
export const alignBlockTop = (block) => ({ ...block, y: 0 });
export const alignBlockCenterY = (block, canvas) => ({
  ...block,
  y: Math.max(0, (toNumber(canvas?.height, 0) - toNumber(block?.height, 0)) / 2),
});
export const alignBlockBottom = (block, canvas) => ({
  ...block,
  y: Math.max(0, toNumber(canvas?.height, 0) - toNumber(block?.height, 0)),
});

export const applyPresetPosition = (block, presetName, canvas, gridSize = 8) => {
  if (!block) return block;
  const margin = Math.max(8, snapToGrid(gridSize * 2, gridSize));
  const maxX = Math.max(0, toNumber(canvas?.width, 0) - toNumber(block.width, 0));
  const maxY = Math.max(0, toNumber(canvas?.height, 0) - toNumber(block.height, 0));
  const centerX = Math.max(0, (toNumber(canvas?.width, 0) - toNumber(block.width, 0)) / 2);
  const centerY = Math.max(0, (toNumber(canvas?.height, 0) - toNumber(block.height, 0)) / 2);

  let next = { ...block };
  switch (String(presetName || '')) {
    case 'center':
      next = { ...next, x: centerX, y: centerY };
      break;
    case 'header':
      next = { ...next, x: centerX, y: margin };
      break;
    case 'footer':
      next = { ...next, x: centerX, y: maxY - margin };
      break;
    case 'top_left':
      next = { ...next, x: margin, y: margin };
      break;
    case 'top_right':
      next = { ...next, x: maxX - margin, y: margin };
      break;
    case 'bottom_left':
      next = { ...next, x: margin, y: maxY - margin };
      break;
    case 'bottom_right':
      next = { ...next, x: maxX - margin, y: maxY - margin };
      break;
    case 'price_badge_corner':
      next = { ...next, x: maxX - margin, y: margin };
      break;
    case 'qr_panel_corner':
      next = { ...next, x: maxX - margin, y: maxY - margin };
      break;
    case 'credential_center':
      next = { ...next, x: centerX, y: centerY };
      break;
    default:
      return clampRect(block, canvas);
  }
  return clampRect(snapRect(next, gridSize), canvas);
};

export const distributeHorizontally = (blocks, canvas, gridSize = 8) => {
  if (!Array.isArray(blocks) || blocks.length < 3) return blocks || [];
  const sorted = [...blocks].sort((a, b) => toNumber(a.x, 0) - toNumber(b.x, 0));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const widthSum = sorted.reduce((sum, block) => sum + toNumber(block.width, 0), 0);
  const range = (toNumber(last.x, 0) + toNumber(last.width, 0)) - toNumber(first.x, 0);
  const gap = (range - widthSum) / Math.max(1, sorted.length - 1);
  let cursor = toNumber(first.x, 0);
  const map = new Map();
  for (const block of sorted) {
    map.set(block.id, clampRect(snapRect({ ...block, x: cursor }, gridSize), canvas));
    cursor += toNumber(block.width, 0) + gap;
  }
  return blocks.map((block) => map.get(block.id) || block);
};

export const distributeVertically = (blocks, canvas, gridSize = 8) => {
  if (!Array.isArray(blocks) || blocks.length < 3) return blocks || [];
  const sorted = [...blocks].sort((a, b) => toNumber(a.y, 0) - toNumber(b.y, 0));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const heightSum = sorted.reduce((sum, block) => sum + toNumber(block.height, 0), 0);
  const range = (toNumber(last.y, 0) + toNumber(last.height, 0)) - toNumber(first.y, 0);
  const gap = (range - heightSum) / Math.max(1, sorted.length - 1);
  let cursor = toNumber(first.y, 0);
  const map = new Map();
  for (const block of sorted) {
    map.set(block.id, clampRect(snapRect({ ...block, y: cursor }, gridSize), canvas));
    cursor += toNumber(block.height, 0) + gap;
  }
  return blocks.map((block) => map.get(block.id) || block);
};
